/**
 * Outlet Slice
 * Redux slice for outlet management state
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { OutletState } from '../../types/outlet';
import {
  createOutlet,
  deleteOutlet,
  fetchOutletLimits,
  fetchOutlets,
  fetchOutletsWithStatsThunk,
  updateOutlet,
} from './outletThunks';

const initialState: OutletState = {
  outlets: [],
  selectedOutletId: null,
  limits: null,
  isLoading: false,
  error: null,
};

const outletSlice = createSlice({
  name: 'outlets',
  initialState,
  reducers: {
    setSelectedOutletId: (state, action: PayloadAction<string | null>) => {
      state.selectedOutletId = action.payload;
      // persist to localstorage
      localStorage.setItem('outlet', JSON.stringify(action.payload));
    },
    clearOutletError: (state) => {
      state.error = null;
    },
    clearOutlets: (state) => {
      state.outlets = [];
      state.selectedOutletId = null;
      state.limits = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch outlets
    builder
      .addCase(fetchOutlets.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOutlets.fulfilled, (state, action) => {
        state.isLoading = false;
        state.outlets = action.payload;
      })
      .addCase(fetchOutlets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch outlets with stats
    builder
      .addCase(fetchOutletsWithStatsThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOutletsWithStatsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.outlets = action.payload;
      })
      .addCase(fetchOutletsWithStatsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch outlet limits
    builder
      .addCase(fetchOutletLimits.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOutletLimits.fulfilled, (state, action) => {
        state.isLoading = false;
        state.limits = action.payload;
      })
      .addCase(fetchOutletLimits.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Create outlet
    builder
      .addCase(createOutlet.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createOutlet.fulfilled, (state, action) => {
        state.isLoading = false;
        state.outlets.push(action.payload);
        if (state.limits) {
          state.limits.currentOutlets += 1;
          state.limits.canCreateOutlet =
            state.limits.maxOutlets === null ||
            state.limits.currentOutlets < state.limits.maxOutlets;
        }
      })
      .addCase(createOutlet.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update outlet
    builder
      .addCase(updateOutlet.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateOutlet.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.outlets.findIndex(
          (o) => o.id === action.payload.id,
        );
        if (index !== -1) {
          state.outlets[index] = action.payload;
        }
      })
      .addCase(updateOutlet.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Delete outlet
    builder
      .addCase(deleteOutlet.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteOutlet.fulfilled, (state, action) => {
        state.isLoading = false;
        state.outlets = state.outlets.filter((o) => o.id !== action.payload);
        if (state.selectedOutletId === action.payload) {
          state.selectedOutletId = null;
        }
        if (state.limits) {
          state.limits.currentOutlets -= 1;
          state.limits.canCreateOutlet =
            state.limits.maxOutlets === null ||
            state.limits.currentOutlets < state.limits.maxOutlets;
        }
      })
      .addCase(deleteOutlet.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setSelectedOutletId, clearOutletError, clearOutlets } =
  outletSlice.actions;

export default outletSlice.reducer;
